/*
 * Author: Axel Antoine
 * mail: ax.antoine@gmail.com
 * website: http://axantoine.com
 * Created on Tue Oct 25 2022
 *
 * Loki, Inria project-team with Université de Lille
 * within the Joint Research Unit UMR 9189
 * CNRS - Centrale Lille - Université de Lille, CRIStAL
 * https://loki.lille.inria.fr
 *
 * Licence: Licence.md
 */

import { HalfedgeDS } from '../core/HalfedgeDS';
import { Vertex } from '../core/Vertex';
import { Halfedge } from '../core/Halfedge';
import { removeEdge } from './removeEdge';

/*
 *         From                            To
 *
 *
 *            o                              o
 *          ↙ ⇅ ↖                          ↙   ↖
 *        ↙   ⇅   ↖                      ↙       ↖
 *      ↙ f1  ⇅  f4 ↖                  ↙           ↖
 *    ↙       ⇅       ↖              ↙               ↖
 *  o ⇄ ⇄ ⇄ ⇄ v ⇄ ⇄ ⇄ ⇄ o          o         f         o
 *    ↘       ⇅       ↗              ↘               ↗
 *      ↘ f2  ⇅  f3 ↗                  ↘           ↗
 *        ↘   ⇅   ↗                      ↘       ↗
 *          ↘ ⇅ ↗                          ↘   ↗
 *            o                              o
 *
 * If all halfedges starting from vertex v to delete are connected to a face,
 * then we create a new face v.
 * If some of the halfedges starting from v are boundaries (i.e. no face),
 * then we can't create a new face.
 *
 */

export function removeVertex(struct: HalfedgeDS, vertex: Vertex, mergeFaces = true) {
  // Collect all halfedges first to avoid issues with iterator invalidation
  // during edge removal which modifies the vertex's halfedge connectivity
  const halfedgesToRemove: Halfedge[] = [];

  // Check if vertex is isolated (no halfedges)
  if (vertex.isIsolated()) {
    struct.vertices.remove(vertex);
    return;
  }

  // Collect all outgoing halfedges from this vertex
  for (const halfedge of vertex.loopCW()) {
    halfedgesToRemove.push(halfedge);
  }

  // Now safely remove all edges
  for (const halfedge of halfedgesToRemove) {
    // Check if halfedge still exists (might have been removed as a twin of another edge)
    if (struct.halfedges.includes(halfedge)) {
      removeEdge(struct, halfedge, mergeFaces);
    }
  }

  // Remove vertex from structure (should be isolated at this point)
  struct.vertices.remove(vertex);
}
